import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { Overline } from "@/components/Bits";
import { useI18n } from "@/i18n";

const FRAME_COUNT = 120;
const frameSrc = (i) => `/hero/ezgif-frame-${String(i).padStart(3, "0")}.jpg`;

export default function ScrollHero() {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const imagesRef = useRef([]);
  const progressRef = useRef(0);
  const targetRef = useRef(0);
  const drawnRef = useRef(-1);
  const [chapter, setChapter] = useState(0);
  const [ready, setReady] = useState(false);
  const { t } = useI18n();

  const CHAPTERS = [
    { at: 0, overline: t("hero.over"), title: (<>{t("hero.title1")}<br /><span className="italic text-zinc-400">{t("hero.title2")}</span></>), body: t("hero.sub") },
    { at: 0.3, overline: t("hero.c2.over"), title: t("hero.c2.title"), body: t("hero.c2.body") },
    { at: 0.58, overline: t("hero.c3.over"), title: t("hero.c3.title"), body: t("hero.c3.body") },
    { at: 0.84, overline: t("hero.c4.over"), title: t("hero.c4.title"), body: t("hero.c4.body") },
  ];

  useEffect(() => {
    let mounted = true;
    const imgs = new Array(FRAME_COUNT);
    const load = (i) => {
      const img = new Image();
      img.decoding = "async";
      img.src = frameSrc(i);
      img.onload = () => {
        if (mounted) setReady(true);
      };
      imgs[i - 1] = img;
    };
    // first pass: keyframes every 4th image so the sequence is scrubbable fast
    for (let i = 1; i <= FRAME_COUNT; i += 4) load(i);
    const rest = window.setTimeout(() => {
      if (!mounted) return;
      for (let i = 1; i <= FRAME_COUNT; i += 1) if (!imgs[i - 1]) load(i);
    }, 400);
    imagesRef.current = imgs;
    return () => {
      mounted = false;
      window.clearTimeout(rest);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d", { alpha: false });
    let raf;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, window.innerWidth < 768 ? 1.5 : 2);
      canvas.width = canvas.clientWidth * dpr;
      canvas.height = canvas.clientHeight * dpr;
      drawnRef.current = -1;
    };

    const draw = (index) => {
      let img = imagesRef.current[index];
      if (!img || !img.complete || !img.naturalWidth) {
        // fall back to the nearest already-decoded frame: no flicker while loading
        for (let d = 1; d <= 6 && (!img || !img.complete || !img.naturalWidth); d += 1) {
          img = imagesRef.current[Math.max(0, index - d)] || imagesRef.current[Math.min(FRAME_COUNT - 1, index + d)];
        }
      }
      if (!img || !img.complete || !img.naturalWidth) return false;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      const cw = canvas.width;
      const ch = canvas.height;
      const scale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      ctx.fillStyle = "#F1F1F2";
      ctx.fillRect(0, 0, cw, ch);
      // shift the subject to the right on wide screens so it never sits behind the copy
      const offsetX = canvas.clientWidth >= 768 ? w * 0.1 : 0;
      ctx.drawImage(img, (cw - w) / 2 + offsetX, (ch - h) / 2, w, h);
      return true;
    };

    const onScroll = () => {
      const el = wrapRef.current;
      if (!el) return;
      const total = el.offsetHeight - window.innerHeight;
      targetRef.current = Math.min(1, Math.max(0, -el.getBoundingClientRect().top / (total || 1)));
    };

    const loop = () => {
      progressRef.current += (targetRef.current - progressRef.current) * 0.12;
      const p = progressRef.current;
      const index = Math.min(FRAME_COUNT - 1, Math.floor(p * (FRAME_COUNT - 1) + 0.0001));
      // only mark as drawn when the frame was actually painted, so the first
      // visible frame appears as soon as it decodes — without waiting for a scroll
      if (index !== drawnRef.current && draw(index)) drawnRef.current = index;
      const next = CHAPTERS.reduce((acc, c, i) => (p >= c.at - 0.02 ? i : acc), 0);
      setChapter((prev) => (prev === next ? prev : next));
      raf = requestAnimationFrame(loop);
    };

    resize();
    onScroll();
    loop();
    window.addEventListener("resize", resize);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("scroll", onScroll);
    };
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const c = CHAPTERS[chapter];

  return (
    <div ref={wrapRef} data-testid="scroll-hero" className="relative h-[300vh] border-b border-black/10 bg-[#F1F1F2] md:h-[420vh]">
      <div className="sticky top-0 h-screen overflow-hidden">
        <canvas ref={canvasRef} data-testid="hero-canvas" className="absolute inset-0 h-full w-full contrast-[1.06] saturate-[0.94]" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/75 to-white/10 md:bg-gradient-to-r md:from-white/95 md:via-white/35 md:to-transparent" aria-hidden="true" />
        <div className="relative mx-auto flex h-full max-w-[1400px] flex-col justify-end px-5 pb-32 md:justify-center md:px-10 md:pb-0">
          <div key={chapter} className="max-w-xl dt-reveal" data-testid={`hero-chapter-${chapter}`}>
            <Overline>{c.overline}</Overline>
            <h1 className="mt-6 font-display text-5xl font-light leading-[0.92] tracking-tighter sm:text-6xl lg:text-[5rem]">
              {c.title}
            </h1>
            <p className="mt-7 max-w-md text-base leading-relaxed text-zinc-600">{c.body}</p>
          </div>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to="/diagnostic-gratuit"
              data-testid="hero-free-diagnostic-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-black px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-700"
            >
              {t("cta.freeDiagnostic")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/science"
              data-testid="hero-science-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-black/20 bg-white/60 px-7 py-3.5 text-sm font-semibold backdrop-blur-sm transition-colors hover:bg-white"
            >
              {t("cta.science")}
            </Link>
          </div>
          <div className="absolute bottom-8 left-5 right-5 flex items-end justify-between md:left-10 md:right-10">
            <div className="flex items-center gap-3">
              <ChevronDown className="h-4 w-4 animate-bounce text-zinc-500" aria-hidden="true" />
              <span className="overline text-[9px]">{t("hero.scroll")}</span>
            </div>
            <div className="hidden items-center gap-4 sm:flex">
              {CHAPTERS.map((x, i) => (
                <span key={x.at} className={`h-[2px] w-10 transition-colors duration-500 ${i <= chapter ? "bg-black" : "bg-black/15"}`} />
              ))}
              <span className="font-mono-dt text-[10px] text-zinc-500">
                {String(chapter + 1).padStart(2, "0")} / 0{CHAPTERS.length}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
